/**
 * A place for a failed mutation to be seen.
 *
 * Sub-contract audit 2026-09-23, severity 1: "28 of 31 mutations have no
 * onError and there is no global handler, so nine role-gated Delete buttons
 * visibly do nothing for anyone who is not a director or manager." The
 * queryClient said as much in its own header — "react-query's default error
 * handling is enough — we don't need a global onError hook for now" — and
 * react-query's default for a mutation with no onError is to swallow it.
 *
 * So the button was pressed, the request was refused, and the screen did not
 * change. The user presses it again. That is the worst failure mode a
 * permission has: indistinguishable from a broken page, and it teaches people
 * the software is unreliable rather than that they lack a permission.
 *
 * The gap got wider on the same day, when twenty-seven more sub-contract
 * writes were given role guards. Every one of those is a button that can now
 * be refused.
 *
 * DELIBERATELY TINY. A pub/sub list of the last few errors, with no
 * dependency and no context provider, because the alternative was leaving it
 * unfixed until somebody had time to choose a toast library. Anything that
 * wants to render them subscribes.
 */
import { ApiError, describeApiError } from './api';
import { reportFaultFromError } from './faultReporter';

/**
 * WHAT FAILED, so the box can say so (2026-10-02).
 *
 * The box was titled "That didn't happen" for every failure in the product.
 * Shane: *"they need to state what didnt happen not what didnt haoppen... if it
 * failed to load it needs to say failed to load! or failed to save or failed to
 * download"*. Each of these is a heading in common.json `errorBox.title`.
 *
 * A mutation does not have to say which it is: the request's method answers it
 * (see actionFor). A call that is not a mutation - a PDF, a print, a file read,
 * a check before anything is sent - names its own, because nothing else can.
 *
 * `check` is not a failure: what was entered needs changing. `not_ready` is not
 * one either: something else has to happen first (a customer approved, a
 * director added). Both say so rather than "Failed to …", which would be false.
 */
export type FailedAction =
  | 'save'
  | 'delete'
  | 'remove'
  | 'archive'
  | 'approve'
  | 'send'
  | 'checkout'
  | 'release'
  | 'activate'
  | 'file'
  | 'load'
  | 'open'
  | 'download'
  | 'print'
  | 'export'
  | 'document'
  | 'read'
  | 'check'
  | 'not_ready';

/** Actions that change something, so "Nothing was saved / changed" is true of them. */
export const WRITE_ACTIONS: ReadonlySet<FailedAction> = new Set<FailedAction>([
  'save', 'delete', 'remove', 'archive', 'approve', 'send', 'checkout', 'release', 'activate', 'file',
]);

export interface MutationErrorEntry {
  id: number;
  /** The typed code where there is one — FORBIDDEN_ROLE, SECTION_DENIED, … */
  code: string | null;
  message: string;
  /** Said in the words of somebody who just pressed a button. */
  friendly: string;
  at: number;
  /** What failed — the box's heading. */
  action: FailedAction;
  /** `POST /api/...` where the failure was a request, for whoever traces it. */
  request: string | null;
  /**
   * The request never reached the platform: no connection, or a reply that was
   * not the platform's own (a proxy answering for an API that is down). Said as
   * "Couldn't reach JeanieIQ", not as the status code nobody can act on.
   */
  unreachable: boolean;
  /** The same failure again collapses into this entry rather than a second one. */
  count: number;
}

type Listener = (entries: MutationErrorEntry[]) => void;

let entries: MutationErrorEntry[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

/** Keep the list short: a wall of stale failures is its own kind of noise. */
const MAX = 3;

function emit(): void {
  for (const l of listeners) l(entries);
}

/*
 * FP34C CLAIM REFUSALS (Kris, 1 Oct 2026).
 *
 * A claim's refusals come back naming its month, its branch and the state it
 * is really in, and saying what to do instead, so the server's sentence is the
 * one to show and these keep it word for word. What they add is the one thing
 * the person cannot tell from it: the page offers only the moves the server
 * listed for that claim, so a claim refused for its STATE means the screen was
 * behind - it moved on after the list was loaded. FP34C_CHANGED says so
 * itself, so it is left alone.
 *
 * Five carry text written for whoever reads the log - a date or month format,
 * an amount in pence when the person typed pounds, a route that did not say
 * who is asking, a request that did not say which version of the claim it was
 * filled from - and those get words of their own.
 *
 * Every other FP34C code is about what was typed: a month already claimed, a
 * declaration date out of range, a reason too short or too long, a field an
 * amendment cannot change, an amendment that changes nothing, a discard with
 * no reason. The server's sentence already says what to fix, and it is shown
 * as sent.
 */
const FP34C_MOVED_ON = new Set([
  'FP34C_NOT_FOUND',
  'FP34C_NOT_DRAFT',
  'FP34C_AMEND_REQUIRED',
  'FP34C_NOT_SUBMITTED',
  'FP34C_RECONCILED_LOCKED',
  'FP34C_NEVER_SUBMITTED',
]);

const OWN_WORDS: Readonly<Record<string, string>> = {
  FP34C_DATE: 'That is not a real date. Choose the day from the calendar and try again.',
  FP34C_PERIOD: 'The month must be a real month, written as YYYY-MM - for example 2026-08.',
  FP34C_RECONCILE_AMOUNT:
    'Enter what the Schedule of Payments paid for the fee and service lines on this claim, in pounds and pence, 0 or more.',
  /*
   * The page always sends the version its form was filled from; this is the
   * refusal for a client that did not. The server's sentence names the field.
   */
  FP34C_VERSION_REQUIRED:
    'This form could not say which version of the claim it was filled from, so nothing was saved. Reload the page and try again.',
  /*
   * Not FP34C's alone: any write that cannot tell who is acting refuses with
   * this code, and the service-side text tells a developer to call
   * stampActor. Whichever screen it surfaces on, the person needs the same
   * two facts: nothing was saved, and what to do about it.
   */
  ACTOR_UNKNOWN:
    'The server could not tell who is signed in, so nothing was saved. Sign out and back in; if it happens again, report it with the code below.',
};

/**
 * What a refusal means to the person who pressed the button.
 *
 * The server's message is written for whoever reads the log. These are the two
 * cases somebody hits by accident rather than by mistake, so they say what to
 * do next instead of restating the code.
 */
function friendlyFor(code: string | null, message: string, error: unknown): string {
  if (code !== null && OWN_WORDS[code] !== undefined) return OWN_WORDS[code];
  if (code !== null && FP34C_MOVED_ON.has(code)) {
    return `This claim moved on after the list was loaded. ${message}`;
  }
  if (code === 'FORBIDDEN_ROLE') {
    /*
     * NAME THE ROLES THE SERVER NAMED (pharmacy audit walk, 2026-10-01).
     *
     * This threw the server's message away and asserted "A director or manager
     * can". `requireRole` always replies `This action requires one of: <roles>`,
     * and those roles are frequently NOT director and manager — voiding a
     * clinical record is director and pharmacist.
     *
     * So a manager who pressed Void on a Yellow Card report was told that a
     * manager could do it and to go and ask one. Measured on the running app:
     * the dialog behind the toast said "requires one of: director, pharmacist"
     * at the same moment. Advice invented in the client was wrong exactly when
     * somebody needed it.
     */
    const says = message.trim();
    /*
     * Only repeat the server's message when it actually NAMES the roles.
     * `requireRole` always does ("This action requires one of: …"), but a
     * refusal from somewhere else may just say "Forbidden", and
     * "Forbidden — ask one of them" names nobody to ask.
     */
    return /requires one of:/i.test(says)
      ? `${says} — ask one of them, or ask for your role to be changed.`
      : 'Your role cannot do that. Ask a director, or ask for your role to be changed.';
  }
  if (code === 'SECTION_DENIED') {
    return 'You do not have access to this section. A director can grant it under Users & Permissions.';
  }
  if (code === 'VALIDATION_ERROR') {
    /*
     * NAME THE FIELD (Reagan, 2026-09-28: "whatever the error is and why it
     * wont save it needs to state that in the error box").
     *
     * This returned the envelope's `message`, which for a validation failure is
     * the fixed string "Invalid request". So a form of eighteen fields refused
     * over one of them said "Invalid request · VALIDATION_ERROR" and left the
     * person to guess which — measured on Add Employee, where the server had
     * plainly said `email: Invalid email`.
     *
     * `describeApiError` reads `details.issues`, which ApiError has carried all
     * along. It was written for this exact failure in September
     * (WEB-VALIDATION-DETAIL-001) and fixed the one form that reported it;
     * every other form kept the generic sentence because nothing had changed
     * HERE, at the handler they all share.
     *
     * The generic sentence stays as the fallback, for a validation error that
     * genuinely carries no issue list.
     */
    return describeApiError(
      error,
      'Some of what was entered is not valid. Check the highlighted fields and try again.',
    );
  }
  return message || 'That did not save. Nothing has been changed.';
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}

/**
 * The same words the toast shows, for a form that reports its own failure
 * beside the button. One wording for both, so the person does not read the
 * refusal one way inline and another way in the corner.
 */
export function friendlyMutationError(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  return friendlyFor(code, messageOf(error), error);
}

/**
 * The heading for a failure nobody named. A request's method says it: GET
 * loads, DELETE deletes, and the writes save, unless the address says it
 * sends. The download and open helpers carry their own codes. With nothing to
 * go on it is a mutation, and a mutation is a write - "Failed to Save" is the
 * true answer far more often than not, and the message underneath says the rest.
 */
function actionFor(api: ApiError | null): FailedAction {
  if (api?.code === 'DOWNLOAD_FAILED') return 'download';
  if (api?.code === 'OPEN_FAILED') return 'open';
  const method = api?.request?.method.toUpperCase();
  if (method === 'GET') return 'load';
  if (method === 'DELETE') return 'delete';
  if (/\/((re)?send(-[a-z-]+)?|order-acknowledgement)$/.test(api?.request?.path ?? '')) return 'send';
  return 'save';
}

/**
 * Did the request get as far as the platform? Not when fetch could not connect
 * (a TypeError from fetch itself, or the NETWORK_ERROR the API client and the
 * upload helpers turn it into), and not when a 5xx came back without the
 * platform's JSON - which the API never does (its terminal handler always
 * sends the envelope), so something in front of it answered instead.
 */
function isUnreachable(error: unknown, api: ApiError | null): boolean {
  if (!api) {
    return error instanceof TypeError && /fetch|network|load failed/i.test(error.message);
  }
  if (api.code === 'NETWORK_ERROR' && (api.status === 0 || api.status >= 500)) return true;
  return api.reachedPlatform === false && (api.status === 0 || api.status >= 500);
}

export function recordMutationError(error: unknown, action?: FailedAction): void {
  const api = error instanceof ApiError ? error : null;
  const message = messageOf(error);
  const code = api?.code ?? null;
  const friendly = friendlyFor(code, message, error);
  const resolvedAction = action ?? actionFor(api);
  const request = api?.request ? `${api.request.method.toUpperCase()} ${api.request.path}` : null;
  const unreachable = isUnreachable(error, api);

  // To the Error Log, if this is a fault only the browser can see - the
  // server could not be reached, or something else answered for it, or the
  // page's own work failed. A refusal, or a fault the server already wrote
  // down, goes no further. See faultReporter.
  reportFaultFromError(error, resolvedAction);

  /*
   * THE SAME FAILURE TWICE IS ONE ENTRY (2026-10-02). Two writes failing for
   * the one reason drew two identical rows - "Request failed with status 500",
   * twice - which reads as two problems when it is one.
   */
  const same = entries.find(
    (e) =>
      e.action === resolvedAction &&
      e.code === code &&
      e.friendly === friendly &&
      e.request === request &&
      e.unreachable === unreachable,
  );
  if (same) {
    entries = [...entries.filter((e) => e.id !== same.id), { ...same, count: same.count + 1, at: Date.now() }];
    emit();
    return;
  }

  const entry: MutationErrorEntry = {
    id: nextId,
    code,
    message,
    friendly,
    at: Date.now(),
    action: resolvedAction,
    request,
    unreachable,
    count: 1,
  };
  nextId += 1;
  entries = [...entries, entry].slice(-MAX);
  emit();
}

const DOCUMENT_FAILURES: Record<string, FailedAction> = { OPEN_FAILED: 'open', DOWNLOAD_FAILED: 'download' };

/** A document that would not open: the person reads the sentence; the code and request are for the Error Log. */
export const isDocumentFailure = (code: string | null): boolean => code !== null && code in DOCUMENT_FAILURES;

/**
 * A document button with no handling of its own: the failure reaches nobody's
 * catch, and the person would see nothing. One handler, so a button added later
 * is covered too; a caller with a catch never arrives here.
 */
export function installDocumentFailureBox(): void {
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const action = e.reason instanceof ApiError ? DOCUMENT_FAILURES[e.reason.code] : undefined;
    if (action) recordMutationError(e.reason, action);
  });
}

/**
 * "THIS ONE SHOWS ITS OWN ERROR" (2026-10-14).
 *
 * The global box exists because 28 of 31 mutations had no onError and failed
 * in silence. It runs IN ADDITION to a mutation's own handler, which is the
 * right trade for the staff product: said twice beats never said.
 *
 * It is the wrong trade on the EMPLOYEE APP. Walking the app as a real
 * employee, a mistyped National Insurance number drew the inline sentence it
 * was meant to — "That doesn't look like a National Insurance number. It's
 * two letters, six numbers, then A, B, C or D." — and then a blocking
 * platform dialog landed on top of it reading "Failed to Save ·
 * INVALID_NI_NUMBER · PUT /api/me/ni-number". An employee on their phone got
 * an API error code and an endpoint path, in the staff product's chrome,
 * over an app that had already explained the problem properly. It also wrote
 * a refusal audit row every time somebody fat-fingered a field.
 *
 * The queryCache beside this one already draws exactly this line for reads —
 * "each screen already shows its own 'Failed to load', and a box on top of
 * it would say it twice." This is that same rule, for writes that ask for it.
 *
 * OPT-IN, NEVER BY ROUTE. Tagging the mutation states something true about
 * the code — this call renders its own failure — and stays true wherever the
 * component is mounted. A path check inside the box would be a guess about a
 * component from the outside, and would also silence mutations on that
 * screen that handle nothing. Use it only where the failure is genuinely put
 * in front of the person; the safety net goes on covering everything else.
 */
export const SHOWS_ITS_OWN_ERROR = { showsItsOwnError: true } as const;

/** Whether a mutation asked to be left out of the global box. */
export function showsItsOwnError(meta: Record<string, unknown> | undefined): boolean {
  return meta?.['showsItsOwnError'] === true;
}

export function dismissMutationError(id: number): void {
  entries = entries.filter((e) => e.id !== id);
  emit();
}

export function subscribeToMutationErrors(listener: Listener): () => void {
  listeners.add(listener);
  listener(entries);
  return () => { listeners.delete(listener); };
}

/** Tests only: forget everything, so one case cannot leak into the next. */
export function resetMutationErrors(): void {
  entries = [];
  nextId = 1;
  emit();
}
